// ADC-IMPLEMENTS: <adc-006a-feature-actions-01>
// ADC-IMPLEMENTS: <adc-006a-dm-incident-action-01>
/**
 * IncidentActions -- Action buttons for changing incident status.
 *
 * ADC-006A: All actions use Dialog (not AlertDialog) because they require
 * text input (F10). Toast pattern follows F9.
 *
 * Valid transitions:
 *   PENDING  -> Resolve, Dismiss
 *   ACTIVE   -> Resolve, Dismiss, Escalate
 *   ESCALATED -> Resolve, Dismiss
 *   RESOLVED / FALSE_POSITIVE -> None (terminal)
 */

import { useState } from 'react';
import { CheckCircle, XCircle, AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { toast } from '@/hooks/use-toast';
import { type Incident, isTerminalStatus } from '@/lib/incidentUtils';

interface IncidentActionsProps {
  incident: Incident;
  onResolve: (notes: string) => Promise<void>;
  onDismiss: (notes: string) => Promise<void>;
  onEscalate: (escalateTo: string) => Promise<void>;
}

export function IncidentActions({
  incident,
  onResolve,
  onDismiss,
  onEscalate,
}: IncidentActionsProps) {
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [dismissDialogOpen, setDismissDialogOpen] = useState(false);
  const [escalateDialogOpen, setEscalateDialogOpen] = useState(false);
  const [notes, setNotes] = useState('');
  const [escalateTo, setEscalateTo] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  if (isTerminalStatus(incident.status)) {
    return null;
  }

  const canEscalate = incident.status === 'ACTIVE';

  const handleResolve = async () => {
    if (!notes.trim()) return;
    setIsLoading(true);
    try {
      await onResolve(notes.trim());
      toast({
        title: 'Incident resolved',
        description: 'The incident has been marked as resolved.',
      });
      setResolveDialogOpen(false);
      setNotes('');
    } catch (err) {
      console.error('Error resolving incident:', err);
      toast({
        title: 'Error',
        description: 'Failed to resolve incident. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDismiss = async () => {
    if (!notes.trim()) return;
    setIsLoading(true);
    try {
      await onDismiss(notes.trim());
      toast({
        title: 'Incident dismissed',
        description: 'The incident has been marked as a false positive.',
      });
      setDismissDialogOpen(false);
      setNotes('');
    } catch (err) {
      console.error('Error dismissing incident:', err);
      toast({
        title: 'Error',
        description: 'Failed to dismiss incident. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEscalate = async () => {
    if (!escalateTo.trim()) return;
    setIsLoading(true);
    try {
      await onEscalate(escalateTo.trim());
      toast({
        title: 'Incident escalated',
        description: `The incident has been escalated to ${escalateTo.trim()}.`,
      });
      setEscalateDialogOpen(false);
      setEscalateTo('');
    } catch (err) {
      console.error('Error escalating incident:', err);
      toast({
        title: 'Error',
        description: 'Failed to escalate incident. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-wrap gap-2">
      {/* Resolve */}
      <Dialog open={resolveDialogOpen} onOpenChange={(open) => { setResolveDialogOpen(open); if (!open) setNotes(''); }}>
        <DialogTrigger asChild>
          <Button variant="outline" size="sm" className="text-green-600 border-green-200 hover:bg-green-50 dark:hover:bg-green-900/20">
            <CheckCircle className="h-4 w-4 mr-1" />
            Resolve
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Resolve Incident</DialogTitle>
            <DialogDescription>
              Provide resolution notes explaining how this incident was addressed.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder="Resolution notes (required)..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="min-h-[100px]"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setResolveDialogOpen(false)} disabled={isLoading}>
              Cancel
            </Button>
            <Button onClick={handleResolve} disabled={!notes.trim() || isLoading}>
              {isLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-1" />}
              Confirm Resolve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dismiss as False Positive */}
      <Dialog open={dismissDialogOpen} onOpenChange={(open) => { setDismissDialogOpen(open); if (!open) setNotes(''); }}>
        <DialogTrigger asChild>
          <Button variant="outline" size="sm" className="text-muted-foreground">
            <XCircle className="h-4 w-4 mr-1" />
            False Positive
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Dismiss as False Positive</DialogTitle>
            <DialogDescription>
              Explain why this incident is a false positive.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder="Explanation notes (required)..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="min-h-[100px]"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDismissDialogOpen(false)} disabled={isLoading}>
              Cancel
            </Button>
            <Button variant="secondary" onClick={handleDismiss} disabled={!notes.trim() || isLoading}>
              {isLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <XCircle className="h-4 w-4 mr-1" />}
              Confirm Dismiss
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Escalate -- only for ACTIVE status */}
      {canEscalate && (
        <Dialog open={escalateDialogOpen} onOpenChange={(open) => { setEscalateDialogOpen(open); if (!open) setEscalateTo(''); }}>
          <DialogTrigger asChild>
            <Button variant="destructive" size="sm">
              <AlertTriangle className="h-4 w-4 mr-1" />
              Escalate
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Escalate Incident</DialogTitle>
              <DialogDescription>
                Specify who this incident should be escalated to.
              </DialogDescription>
            </DialogHeader>
            <Input
              placeholder="Escalation target (e.g., parent, supervisor)..."
              value={escalateTo}
              onChange={(e) => setEscalateTo(e.target.value)}
            />
            <DialogFooter>
              <Button variant="outline" onClick={() => setEscalateDialogOpen(false)} disabled={isLoading}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleEscalate} disabled={!escalateTo.trim() || isLoading}>
                {isLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <AlertTriangle className="h-4 w-4 mr-1" />}
                Confirm Escalate
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
